import { useEffect, useRef, useState } from 'react';
import {
  checkContactImport,
  commitContactImport,
  previewContactImport,
  type CheckedImportRow,
  type ContactImportPreview,
  type ContactImportResult,
  type DataSource,
} from '../api.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';

/**
 * Bringing in a list of contacts somebody already has.
 *
 * Nobody types two hundred suppliers into a form. They arrive in a
 * spreadsheet, with whatever column names the person who made it chose.
 *
 * So the file is read, what was understood is shown, and nothing is sent until
 * it is agreed. The step in the middle is the point: an import that half
 * worked leaves somebody comparing a spreadsheet against Xero by eye, which is
 * exactly the job this exists to remove.
 */

/** The fields a column can feed, in the order the form shows them. */
const FIELDS: { key: string; label: string }[] = [
  { key: 'name', label: 'Contact name' },
  { key: 'accountNumber', label: 'Account number' },
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'street', label: 'Street' },
  { key: 'city', label: 'City' },
  { key: 'region', label: 'Region' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'country', label: 'Country' },
  { key: 'taxNumber', label: 'Tax number' },
  { key: 'bankAccount', label: 'Bank account' },
  { key: 'defaultCurrency', label: 'Currency' },
];

const PROBLEMS: Record<string, string> = {
  no_name: 'No name',
  duplicate_in_file: 'Listed twice in this file',
  already_in_xero: 'Already in Xero',
  bad_email: 'Not an email address',
};

export function ImportContactsScreen({
  source,
  t,
  onBack,
  onDone,
}: {
  source: DataSource;
  t: Translate;
  onBack: () => void;
  onDone: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ContactImportPreview | null>(null);
  const [columns, setColumns] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ContactImportResult | null>(null);

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setFileName(file.name);
    try {
      const read = await previewContactImport(file, source);
      setPreview(read);
      setColumns(read.columns as Record<string, number>);
    } catch (caught) {
      setPreview(null);
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const [rows, setRows] = useState<CheckedImportRow[]>([]);

  /*
    Checked again whenever the mapping moves, and by the server rather than
    here.

    Move the name column and every answer changes with it — which row has no
    name, which duplicates another, which one Xero already holds. Answering
    once at upload and letting the mapping change underneath would leave
    confident, stale warnings on screen. The file itself is not re-read: the
    cells are already in hand, and reading twice is a second chance to
    understand them differently.
  */
  const mappingKey = JSON.stringify(columns);
  useEffect(() => {
    if (!preview) {
      setRows([]);
      return;
    }
    let cancelled = false;
    checkContactImport(preview.raw, columns, source)
      .then((checked) => !cancelled && setRows(checked))
      .catch(() => !cancelled && setRows([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, mappingKey, source]);

  const ready = rows.filter((row) => row.problems.length === 0);
  const blocked = rows.length - ready.length;

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await commitContactImport(ready.map((row) => row.fields), source);
      setResult(outcome);
      setPreview(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1000px' }}>
      <button
        type="button"
        onClick={onBack}
        style={{ background: 'none', border: 0, color: theme.ink, fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '12px' }}
      >
        {t('← Back')}
      </button>

      <PageTitle
        title={t('Import contacts')}
        sub={t('A spreadsheet of customers or suppliers. Nothing is sent to Xero until you have seen what was understood.')}
      />
      <div style={{ height: '16px' }} />

      <Card>
        <div style={{ padding: '18px 16px', display: 'grid', gap: '14px' }}>
          <div style={{ fontSize: '12.5px', color: theme.textMuted, lineHeight: 1.7 }}>
            {t('Excel (.xlsx) or CSV. Excel is safer for Arabic: saving a CSV from Excel on an Arabic machine can change the encoding and the names arrive unreadable.')}
          </div>

          {/*
            Said here because it is the first thing anyone asks, and because
            there is no checkbox for it to be missing from.
          */}
          <div style={{ fontSize: '12.5px', color: theme.textMuted, lineHeight: 1.7 }}>
            {t('Xero decides whether a contact is a customer or a supplier, from whether it has an invoice or a bill. Both lists import the same thing.')}
          </div>

          <input
            ref={input}
            type="file"
            hidden
            accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => {
              void choose(event.target.files?.[0]);
              event.target.value = '';
            }}
          />

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => input.current?.click()}
              style={{
                background: busy ? theme.borderStrong : theme.nav,
                color: '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                padding: '10px 18px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              {busy ? t('Reading…') : t('Choose a file')}
            </button>

            <a
              href="/api/contacts/import/template"
              style={{
                border: `1px solid ${theme.borderStrong}`,
                borderRadius: theme.radiusControl,
                padding: '9px 15px',
                fontSize: '12.5px',
                fontWeight: 600,
                color: theme.text,
                textDecoration: 'none',
              }}
            >
              {t('Download a template')}
            </a>

            {fileName && <span style={{ fontSize: '12px', color: theme.textMuted }}>{fileName}</span>}
          </div>

          {error && (
            <div style={{ fontSize: '12.5px', color: theme.badDeep, fontWeight: 600 }}>{t(error)}</div>
          )}

          {preview?.encoding === 'windows-1256' && (
            <div style={{ fontSize: '12.5px', color: theme.badDeep, fontWeight: 600, lineHeight: 1.6 }}>
              {t('This file was saved in the Arabic Windows encoding, not Unicode. The names below have been converted — check them. If any look wrong, re-save from Excel as "CSV UTF-8" or as .xlsx.')}
            </div>
          )}
        </div>
      </Card>

      {preview && (
        <>
          <div style={{ height: '18px' }} />
          <Card>
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${theme.dividerSoft}`,
                fontWeight: 600,
                fontSize: '13px',
                background: theme.surfaceHeader,
              }}
            >
              {t('Which column is which')}
            </div>
            <div
              style={{
                padding: '16px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                gap: '12px',
              }}
            >
              {FIELDS.map((field) => (
                <label key={field.key} style={{ display: 'grid', gap: '4px' }}>
                  <span style={{ fontSize: '11.5px', fontWeight: 600, color: theme.textMuted }}>
                    {t(field.label)}
                    {field.key === 'name' && <span style={{ color: theme.badDeep }}> *</span>}
                  </span>
                  <select
                    value={columns[field.key] ?? ''}
                    onChange={(event) =>
                      setColumns((current) => {
                        const next = { ...current };
                        if (event.target.value === '') delete next[field.key];
                        else next[field.key] = Number(event.target.value);
                        return next;
                      })
                    }
                    style={{
                      border: `1px solid ${theme.borderStrong}`,
                      borderRadius: theme.radiusControl,
                      padding: '7px 9px',
                      fontSize: '12.5px',
                      fontFamily: 'inherit',
                      background: theme.surface,
                      color: 'inherit',
                    }}
                  >
                    <option value="">{t('— not imported —')}</option>
                    {preview.headings.map((heading, at) => (
                      <option key={at} value={at}>
                        {heading || `${t('Column')} ${at + 1}`}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </Card>

          <div style={{ height: '18px' }} />
          <Card>
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${theme.dividerSoft}`,
                display: 'flex',
                gap: '14px',
                alignItems: 'baseline',
                background: theme.surfaceHeader,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: '13px' }}>{t('What will be imported')}</span>
              <span style={{ fontSize: '12px', color: theme.good, fontWeight: 600 }}>
                {ready.length} {t('ready')}
              </span>
              {blocked > 0 && (
                <span style={{ fontSize: '12px', color: theme.badDeep, fontWeight: 600 }}>
                  {blocked} {t('will be skipped')}
                </span>
              )}
            </div>

            <div style={{ maxHeight: '420px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.borderStrong}` }}>
                    <Th>{t('Line')}</Th>
                    <Th align="start">{t('Contact name')}</Th>
                    <Th align="start">{t('Email')}</Th>
                    <Th align="start">{t('Phone')}</Th>
                    <Th align="start">{t('City')}</Th>
                    <Th align="start">{t('Status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const bad = row.problems.length > 0;
                    return (
                      <tr
                        key={row.line}
                        style={{ borderBottom: `1px solid ${theme.divider}`, opacity: bad ? 0.62 : 1 }}
                      >
                        <td dir="ltr" style={{ padding: '8px 6px', textAlign: 'center', fontFamily: theme.fontMono, color: theme.textMuted }}>
                          {row.line}
                        </td>
                        <td style={{ padding: '8px 6px', fontWeight: bad ? 400 : 600 }}>{row.fields.name || '—'}</td>
                        <td dir="ltr" style={{ padding: '8px 6px', color: theme.textMuted }}>{row.fields.email}</td>
                        <td dir="ltr" style={{ padding: '8px 6px', color: theme.textMuted }}>{row.fields.phone}</td>
                        <td style={{ padding: '8px 6px', color: theme.textMuted }}>{row.fields.city}</td>
                        <td style={{ padding: '8px 6px', color: bad ? theme.badDeep : theme.good, fontWeight: 600 }}>
                          {bad ? row.problems.map((p) => t(PROBLEMS[p] ?? p)).join(', ') : t('Ready')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ padding: '14px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={busy || ready.length === 0}
                onClick={() => void send()}
                style={{
                  background: busy || ready.length === 0 ? theme.borderStrong : theme.good,
                  color: '#fff',
                  border: 0,
                  borderRadius: theme.radiusControl,
                  padding: '11px 22px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: busy || ready.length === 0 ? 'default' : 'pointer',
                }}
              >
                {busy ? t('Sending…') : `${t('Import')} ${ready.length}`}
              </button>
              {blocked > 0 && (
                <span style={{ fontSize: '12px', color: theme.textMuted }}>
                  {t('Skipped rows are left alone. Fix them in the file and import it again.')}
                </span>
              )}
            </div>
          </Card>
        </>
      )}

      {result && (
        <>
          <div style={{ height: '18px' }} />
          <Card>
            <div style={{ padding: '18px 16px', display: 'grid', gap: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: theme.good }}>
                {result.created.length} {t('contacts added to Xero.')}
              </div>

              {result.refused.length > 0 && (
                <div style={{ display: 'grid', gap: '6px' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: theme.badDeep }}>
                    {result.refused.length} {t('were refused by Xero:')}
                  </div>
                  {result.refused.map((row, at) => (
                    <div key={at} style={{ fontSize: '12px', color: theme.textMuted }}>
                      <strong>{row.name || '—'}</strong> — {row.reason}
                    </div>
                  ))}
                </div>
              )}

              <div>
                <button
                  type="button"
                  onClick={onDone}
                  style={{
                    background: theme.nav,
                    color: '#fff',
                    border: 0,
                    borderRadius: theme.radiusControl,
                    padding: '10px 18px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginTop: '4px',
                  }}
                >
                  {t('See the list')}
                </button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Th({ children, align = 'center' }: { children: React.ReactNode; align?: 'start' | 'center' }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: '9px 6px',
        fontSize: '10.5px',
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: theme.textSubtle,
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        background: theme.surface,
      }}
    >
      {children}
    </th>
  );
}
